import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Mail } from "lucide-react";
import { useAgentAuth } from "../../contexts/AgentAuthContext";
import { agentEmail } from "../../utils/api";
import { clearCapturedEmailChangeToken, getCapturedEmailChangeToken } from "../../utils/agentLoginBootstrap";

const AgentEmailChangeConfirmation = () => {
  const navigate = useNavigate();
  const { agent, loading, verifyAgentToken } = useAgentAuth();
  // Bootstrap has already removed the credential before telemetry or React starts.
  // Move it into this component's memory; refresh/Back never restores it.
  const [token, setToken] = useState(getCapturedEmailChangeToken);
  const [status, setStatus] = useState("ready");
  const [error, setError] = useState("");
  const [sessionRefreshed, setSessionRefreshed] = useState(false);
  const inFlight = useRef(false);
  const headingRef = useRef(null);

  useEffect(() => { clearCapturedEmailChangeToken(); }, []);
  useEffect(() => {
    if (status === "success" || status === "error") headingRef.current?.focus();
  }, [status]);

  const confirmChange = async () => {
    if (!token || !agent || loading || inFlight.current || status !== "ready") return;
    inFlight.current = true;
    setStatus("confirming");
    const confirmationToken = token;
    setToken(null);
    try {
      await agentEmail.confirmChange(confirmationToken);
      // The server has rotated this browser's cookie and revoked other sessions.
      // Refresh the existing context rather than maintaining a second auth store.
      try {
        const result = await verifyAgentToken();
        setSessionRefreshed(Boolean(result?.success));
      } catch (_error) {
        setSessionRefreshed(false);
      }
      setStatus("success");
    } catch (requestError) {
      const statusCode = requestError.response?.status;
      setError(!statusCode || statusCode >= 500
        ? "We couldn’t verify the result. Check Email settings to see whether the address changed before trying again."
        : statusCode === 403
          ? "This request couldn’t be verified. Return to Email settings in the HelloPC app and request a new link."
          : "This link is expired, canceled, already used, or belongs to another browser session. Check Email settings, then reopen the latest email in the browser that requested it or request a new link.");
      setStatus("error");
    } finally {
      inFlight.current = false;
    }
  };

  const leaveConfirmation = () => {
    setToken(null);
    clearCapturedEmailChangeToken();
    navigate("/agent/email", { replace: true });
  };

  const ready = status === "ready";
  return (
    <main id="agent-email-confirmation" className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 px-4 py-12">
      <section id="agent-email-confirmation-card" className="mx-auto max-w-md rounded-xl border border-gray-200 bg-white p-6 shadow-sm" aria-busy={loading || status === "confirming"}>
        <Mail className="mb-4 h-8 w-8 text-blue-600" aria-hidden="true" />
        <h1 ref={headingRef} tabIndex={-1} className="text-2xl font-bold text-gray-900">{status === "success" ? "Email changed" : "Confirm your email change"}</h1>
        {loading && ready && <p role="status" className="mt-4 text-sm text-gray-600">Checking your sign-in session…</p>}
        {ready && token && <p className="mt-3 text-sm text-gray-600">Your current email stays active until you confirm. Confirming verifies the new address, signs out other devices, and keeps this browser signed in with a new session.</p>}
        {ready && !token && <p role="alert" className="mt-4 text-sm text-gray-700">This confirmation link is missing or was cleared when you left or refreshed this page. Reopen the latest verification email in the browser that requested the change, or return to Email settings to request a new link.</p>}
        {ready && token && !loading && !agent && <p role="alert" className="mt-4 text-sm text-gray-700">Use the same signed-in browser where you requested this change. If that session has ended, sign in with your current email and request a new verification email.</p>}
        {ready && token && agent && <>
          <button type="button" onClick={confirmChange} disabled={loading} className="mt-6 w-full rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">Confirm email change</button>
          <button type="button" onClick={leaveConfirmation} className="mt-3 w-full rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700">Not now</button>
          <p className="mt-3 text-xs text-gray-500">Not now leaves your current email unchanged. You can cancel the pending request in Email settings.</p>
        </>}
        {status === "confirming" && <p role="status" className="mt-6 flex items-center text-sm text-gray-600"><Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />Confirming email change…</p>}
        {status === "error" && <p role="alert" className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-800">{error}</p>}
        {status === "success" && <p role="status" className="mt-4 text-sm text-gray-700">Your new email is verified. Other devices have been signed out.{sessionRefreshed ? " This browser is ready to continue." : " Sign in with your new email to continue."}</p>}
        {status !== "confirming" && <nav aria-label="Email change recovery" className="mt-6 flex flex-col gap-3 text-sm">
          <Link to="/agent/email" replace className="text-blue-600 underline">Return to Email settings</Link>
          {!loading && (!agent || (status === "success" && !sessionRefreshed)) && <Link to="/agent/login" state={{ from: { pathname: "/agent/email" } }} replace className="text-blue-600 underline">Sign in again</Link>}
        </nav>}
      </section>
    </main>
  );
};

export default AgentEmailChangeConfirmation;
